import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, Search, ShieldCheck, Loader2, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import IpWhitelistTableSkeleton from "@/shared/components/skeletons/IpWhitelistTableSkeleton";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { auth } from "@/shared/api/graphqlClient";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import * as adminService from "@/shared/api/services/adminService";

export default function AdminIpWhitelist() {
  const { t } = useLanguage();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const queryClient = useQueryClient();
  const pageSize = 15;

  const { data: merchants = [], isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["merchants", "ip-whitelist"],
    queryFn: () => adminService.getAllMerchants(),
    enabled: auth.isAuthenticated(),
    retry: 2,
  });

  const filteredMerchants = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return merchants;
    return merchants.filter((merchant) => {
      const nameMatch = merchant.name?.toLowerCase().includes(term);
      const emailMatch = merchant.email?.toLowerCase().includes(term);
      return nameMatch || emailMatch;
    });
  }, [merchants, search]);

  const paginatedMerchants = useMemo(
    () => filteredMerchants.slice(page * pageSize, (page + 1) * pageSize),
    [filteredMerchants, page]
  );
  const totalPages = Math.ceil(filteredMerchants.length / pageSize);

  const updateStatusMutation = useMutation({
    mutationFn: ({ merchantId, ip, status }) =>
      adminService.updateMerchantIpStatus(merchantId, ip, status),
    onSuccess: () => {
      queryClient.invalidateQueries(["merchants", "ip-whitelist"]);
    },
  });

  const toggleStatus = (merchantId, entry) => {
    const current = entry.statusIp || "active";
    const nextStatus = current === "active" ? "inactive" : "active";
    updateStatusMutation.mutate({ merchantId, ip: entry.ip, status: nextStatus });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center justify-between sm:block">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-blue-600" />
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{t("ipWhitelist")}</h1>
            </div>
            <p className="text-sm text-slate-500">
              {filteredMerchants.length} {t("merchants")}
            </p>
          </div>

          <Button
            variant="outline"
            onClick={() => refetch()}
            disabled={isLoading || isFetching}
            className="sm:hidden shrink-0 border-slate-200"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading || isFetching ? "animate-spin" : ""}`} />
            {t("refresh") || "Refresh"}
          </Button>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            onClick={() => refetch()}
            disabled={isLoading || isFetching}
            className="hidden sm:flex shrink-0 border-slate-200"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading || isFetching ? "animate-spin" : ""}`} />
            {t("refresh") || "Refresh"}
          </Button>
          <div className="relative max-w-md w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder={t("searchMerchants")}
              className="pl-9"
            />
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
        {isLoading ? (
          <IpWhitelistTableSkeleton rows={5} />
        ) : error ? (
          <div className="p-8 text-sm text-rose-600">{error.message || "Failed to load"}</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("merchants")}</TableHead>
                <TableHead>{t("email")}</TableHead>
                <TableHead>{t("ipWhitelist")}</TableHead>
                <TableHead className="text-right">{t("total") || "Total"}</TableHead>
                <TableHead className="text-right">{t("actions") || "Actions"}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMerchants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                    {t("noMerchantsFound")}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedMerchants.map((merchant) => {
                  return (
                    <TableRow key={merchant.id}>
                      <TableCell>
                        <div className="font-medium text-slate-900">{merchant.name || "-"}</div>
                        <div className="text-xs text-slate-500">{merchant.code || merchant.id}</div>
                      </TableCell>
                      <TableCell>{merchant.email}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          {merchant.ipWhitelistEntries?.length === 0 ? (
                            <span className="text-xs text-slate-400">-</span>
                          ) : (
                            merchant.ipWhitelistEntries?.map((entry) => (
                              <span
                                key={`${merchant.id}-${entry.ip}`}
                                className="inline-flex items-center rounded-full bg-slate-100 px-2 py-1 text-xs font-mono text-slate-700"
                              >
                                {entry.ip}
                              </span>
                            ))
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-sm text-slate-600">
                        {merchant.ipWhitelistEntries?.length}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="icon">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-72">
                            {merchant.ipWhitelistEntries?.length === 0 ? (
                              <DropdownMenuItem className="text-slate-500">-</DropdownMenuItem>
                            ) : (
                              merchant.ipWhitelistEntries?.map((entry) => (
                                <DropdownMenuItem
                                  key={`${merchant.id}-${entry.ip}-action`}
                                  className="flex items-center justify-between gap-3"
                                >
                                  <div className="flex flex-col">
                                    <span className="text-xs font-mono text-slate-700">{entry.ip}</span>
                                    <span className="text-[11px] text-slate-500">
                                      {(entry.statusIp || "active") === "active" ? t("active") : t("inactive")}
                                    </span>
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => toggleStatus(merchant.id, entry)}
                                    disabled={updateStatusMutation.isLoading}
                                  >
                                    {(entry.statusIp || "active") === "active" ? t("disable") : t("activate")}
                                  </Button>
                                </DropdownMenuItem>
                              ))
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            {t("showing")} {page * pageSize + 1} {t("to")} {Math.min((page + 1) * pageSize, filteredMerchants.length)} {t("of")} {filteredMerchants.length} {t("merchants")}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-slate-600">{t("page")} {page + 1} {t("of")} {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
